<i18n>
{
  "fr": {
    "log-action-CREATE-label": "Création",
    "log-action-UPDATE-label": "Modification",
    "log-action-communication-label": "Communication",
    "log-action-storage-opened-label": "Ouverture du stockage",
    "log-action-reminder-label": "Relance",
    "log-action-STATUS_CHANGE-label": "Changement de statut",
    "from": "De",
    "to": "à",
    "status-undefined": "Non défini",
    "status-waiting": "En attente de téléversement",
    "status-cancel": "Annulé",
    "status-running": "En cours",
    "status-done": "Terminé"
  }
}
</i18n>

<template>

  <div class="map-container">
    <div id="map" :class="this.drawState ? 'map no-cusror' : 'map'"></div>
    <div class="map-control-container">
      <MapControl
        v-if="!(disabled || drawDisabled)"
        iconName="edit"
        type="toggle"
        v-on:change="setDrawState"
      />
      <MapControl
        v-if="selectedFeature !== null && !disabled"
        iconName="delete_outline"
        v-on:click="deleteSelectedFeature"
      />
    </div>
  </div>

</template>

<script>

import _ from 'lodash'

import { Map, View } from 'ol'
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer'
import { Vector as VectorSource } from 'ol/source'
import { get as getProjection } from 'ol/proj'
import { Draw, Select, Modify } from 'ol/interaction'
import { createBox } from 'ol/interaction/Draw'
import { Fill, Stroke, Style } from 'ol/style'

import { GeoJSON } from '@/helpers/GeoJSON'
import Swisstopo from '@/helpers/swisstopo'

import MapControl from '@/components/map/map-control'

const DEFAULT_DRAW_METHOD = 'Polygon'
const DEFAULT_CENTER = [2605283.8935783166, 1115618.9828508834]

export default {
  name: 'Map',
  components: {
    MapControl
  },
  props: {
    drawGeom: {
      type: String
    },
    drawDisabled: {
      type: Boolean,
      default: () => { return false }
    },
    drawMethod: {
      type: String,
      default: () => { return DEFAULT_DRAW_METHOD }
    },
    disabled: {
      type: Boolean,
      default: () => { return false }
    }
  },
  event: 'change',
  data () {
    return {
      map: null,
      draw: {
        interaction: null,
        source: null
      },
      select: {
        interaction: null
      },
      isDrawDisabled: this.drawDisabled,
      selectedFeature: null,
      drawState: false
    }
  },
  watch: {
    drawDisabled (val) {
      if (val) this.setDrawState(false)
    }
  },
  methods: {
    initMap (config) {
      this.draw = {
        source: new VectorSource({
          wrapX: false,
          features: this.drawGeom === '' || this.drawGeom === null ? null : GeoJSON.readFeatures(JSON.parse(this.drawGeom), {
            dataProjection: getProjection('EPSG:2056'),
            featureProjection: getProjection('EPSG:2056')
          })
        })
      }

      let vectorLayer = new VectorLayer({
        source: this.draw.source,
        style: new Style({
          fill: new Fill({
            color: 'rgba(214, 58, 58, 0.3)'
          })
        })
      })

      this.map = new Map({
        controls: [],
        layers: [
          new TileLayer({
            opacity: 0.8,
            source: Swisstopo.getWmtsSource()
          }),
          new TileLayer({
            opacity: 0.2,
            source: Swisstopo.getWmtsSource({
              serverLayerName: 'ch.swisstopo.swissimage'
            })
          }),
          vectorLayer
        ],
        target: 'map',
        view: new View({
          center: DEFAULT_CENTER,
          projection: Swisstopo.getUsedProjection(),
          zoom: 5
        })
      })

      if (typeof config.extent !== 'undefined' && config.extent !== null) this.map.getView().fit(config.extent, { padding: [120, 120, 120, 120] })

      this.enableSelect()

      this.draw.source.on('addfeature', (e) => {
        this.onFeaturesChange()
      })
    },
    repaint () {
      setTimeout(() => {
        this.map.updateSize()
      }, 100)
    },
    onFeaturesChange () {
      let toEmit = GeoJSON.writeFeatures(this.draw.source.getFeatures(), {
        dataProjection: getProjection('EPSG:2056'),
        featureProjection: getProjection('EPSG:2056')
      })
      this.$emit('drawChange', toEmit)
    },
    setDrawState (state) {
      this.drawState = state
      if (state) {
        this.enableDraw()
        this.disableSelect()
        return
      }
      this.disableDraw()
      this.enableSelect()
    },
    enableDraw (option = this.drawMethod) {
      if (this.isDrawDisabled || this.disabled) return

      switch (option) {
        case 'Polygon':
          this.draw.interaction = new Draw({
            source: this.draw.source,
            type: option
          })
          break
        case 'Box':
          this.draw.interaction = new Draw({
            source: this.draw.source,
            type: 'Circle',
            geometryFunction: createBox()
          })
          break
        default:
          return
      }

      this.draw.modifyInteraction = new Modify({ source: this.draw.source })
      this.draw.modifyInteraction.on('modifyend', (e) => {
        this.onFeaturesChange()
      })

      this.map.addInteraction(this.draw.interaction)
      this.map.addInteraction(this.draw.modifyInteraction)
    },
    disableDraw () {
      if (this.draw.interaction === null) return
      this.map.removeInteraction(this.draw.interaction)
      this.map.removeInteraction(this.draw.modifyInteraction)
    },
    enableSelect () {
      if (this.disabled) return
      this.select.interaction = new Select({
        style: new Style({
          fill: new Fill({
            color: 'rgba(214, 58, 58, 0.6)'
          }),
          stroke: new Stroke({
            color: 'rgba(0, 0, 0, 0.4)',
            width: 3
          })
        })
      })
      this.map.addInteraction(this.select.interaction)
      this.select.interaction.on('select', (e) => {
        let toSet = _.head(e.target.getFeatures().getArray())
        this.selectedFeature = typeof toSet === 'undefined' ? null : toSet
      })
    },
    disableSelect () {
      if (this.select.interaction === null) return
      this.map.removeInteraction(this.select.interaction)
    },
    deleteSelectedFeature () {
      this.draw.source.removeFeature(this.selectedFeature)
      this.selectedFeature = null
      this.onFeaturesChange()
    }
  }
}
</script>

<style lang="scss">
  .no-cusror {
    cursor: none;
  }

  .map-control-container {

    position: absolute;
    top: 25px;
    left: 10px;

    .map-control {
      button {
        min-width: 0px;
        width: 30px;
        height: 30px;
        padding: 0px;
      }
    }
  }
</style>
